import { create } from 'zustand';
// Import the client-side middleware browser
import { createMiddlewareBrowserClient } from '@/lib/supabase/client';
import { isDemoAccount } from '@/lib/tenant/session';

export interface TenantSubscription {
  id: string;
  tenant_id: string;
  stripe_subscription_id: string | null;
  price_id: string;
  status: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean | null;
  created_at: string;
  updated_at: string;
  plan_id: string;
  plan_price_id: string;
  plans?: {
    name: string;
    interval: string;
  } | null;
}
export interface PlanPrice {
  id: string;
  amount: number;
  currency: string;
  stripe_price_id: string;
}
export interface Plan {
  id: string;
  name: string;
  description: string;
  stripe_product_id: string;
  interval: string;
  prices: PlanPrice[];
  perks: Record<string, any>;
}
export interface PlansResult {
  plans: Plan[];
  discountPercent: number;
}

type SubscriptionState = {
  subscription: TenantSubscription | null;
  subscriptionLoading: boolean;
  error: string | null;
  subscriptionHistory: TenantSubscription[];
  plans: Plan[];
  plansLoading: boolean;
  discountPercent: number;
  fetchPlans: (tenantId: string) => Promise<void>;
  fetchSubscriptionHistory: (tenantId: string) => Promise<void>;
  fetchActiveSubscription: (tenantId: string) => Promise<void>;
  clearSubscription: () => void;
  markExpired: (subscriptionId: string) => Promise<void>;
};

export const useSubscriptionStore = create<SubscriptionState>((set) => ({
  subscription: null,
  subscriptionLoading: false,
  error: null,

  plans: [],
  plansLoading: false,
  discountPercent: 0,
  fetchPlans: async (tenantId: string) => {
    set({ plansLoading: true });
    try {
      const url = `https://partners.nxcut.com/api/plans?tenant_id=${encodeURIComponent(tenantId)}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) {
        set({
          plans: data.payload?.plans ?? [],
          discountPercent: data.payload?.discount_percent ?? 0,
        });
      }
    } catch (err: any) {
      console.error('[SubscriptionStore] Failed to fetch plans:', err.message);
    } finally {
      set({ plansLoading: false });
    }
  },

  subscriptionHistory: [],
  fetchSubscriptionHistory: async (tenantId: string) => {
    if (!tenantId) return;
    const supabase = createMiddlewareBrowserClient() as any;
    const { data, error } = await supabase
      .from('tenant_subscriptions')
      .select(
        `
      id, tenant_id, status, current_period_start, current_period_end,
      created_at, plan_id, plan_price_id,
      plans(name, interval),
      plan_prices:plan_price_id(amount, currency)
    `,
      )
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });

    if (!error && data) {
      const history = (data as unknown as TenantSubscription[]).map((sub) => {
        const isExpired = sub.current_period_end
          ? new Date(sub.current_period_end).getTime() < Date.now()
          : false;

        return {
          ...sub,
          status: isExpired ? 'inactive' : sub.status,
        };
      });

      set({ subscriptionHistory: history });
    }
  },

  fetchActiveSubscription: async (tenantId: string) => {
    if (!tenantId) return;

    // Check if the tenant is a demo account
    if (isDemoAccount()) {
      set({
        subscription: {
          id: 'demo-subscription',
          tenant_id: tenantId,
          stripe_subscription_id: null,
          price_id: 'demo-price',
          status: 'active',
          current_period_start: new Date().toISOString(),
          current_period_end: new Date(
            Date.now() + 365 * 86400000 * 10,
          ).toISOString(),
          cancel_at_period_end: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          plan_id: 'demo-plan',
          plan_price_id: 'demo-price',
        },
        subscriptionLoading: false,
        error: null,
      });
      return;
    }

    set({ subscriptionLoading: true, error: null });

    try {
      const supabase = createMiddlewareBrowserClient() as any;

      // Fetch active subscription
      const { data: activeData, error: activeError } = await supabase
        .from('tenant_subscriptions')
        .select(
          `id, tenant_id, stripe_subscription_id, price_id, status, current_period_start, current_period_end, cancel_at_period_end, created_at, updated_at, plan_id, plan_price_id, plans(name, interval)`,
        )
        .eq('tenant_id', tenantId)
        .in('status', ['active','inactive'])
        .lte('current_period_start', new Date().toISOString())
        .order('current_period_end', { ascending: false, nullsFirst: false })
        .limit(1)
        .maybeSingle();

      if (activeError) throw activeError;

      if (activeData) {
        const sub = activeData as unknown as TenantSubscription;
        // Check if current date is past current_period_end
        const isExpired = sub.current_period_end
          ? new Date(sub.current_period_end).getTime() < Date.now()
          : false;

        set({
          subscription: {
            ...sub,
            status: isExpired ? 'inactive' : sub.status,
          },
        });
        return;
      }

      // Fallback to latest subscription row
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('tenant_subscriptions')
        .select(
          `id, tenant_id, stripe_subscription_id, price_id, status, current_period_start, current_period_end, cancel_at_period_end, created_at, updated_at, plan_id, plan_price_id, plans(name, interval)`,
        )
        .eq('tenant_id', tenantId)
        .order('current_period_end', { ascending: false, nullsFirst: false })
        .limit(1)
        .maybeSingle();

      if (fallbackError) throw fallbackError;

      if (fallbackData) {
        const sub = fallbackData as unknown as TenantSubscription;
        const isExpired = sub.current_period_end
          ? new Date(sub.current_period_end).getTime() < Date.now()
          : false;

        set({
          subscription: {
            ...sub,
            status: isExpired ? 'inactive' : sub.status,
          },
        });
      } else {
        set({ subscription: null });
      }
    } catch (err: any) {
      console.error(
        '[SubscriptionStore] Error fetching subscription:',
        err.message,
      );
      set({ error: err.message });
    } finally {
      set({ subscriptionLoading: false });
    }
  },

  clearSubscription: () => set({ subscription: null, error: null }),
  markExpired: async (subscriptionId: string) => {
    const supabase = createMiddlewareBrowserClient() as any;
    await supabase
      .from('tenant_subscriptions')
      .update({ status: 'inactive', updated_at: new Date().toISOString() })
      .eq('id', subscriptionId);
    // Optimistic local update
    set((state) => ({
      subscription:
        state.subscription?.id === subscriptionId
          ? { ...state.subscription, status: 'inactive' }
          : state.subscription,
    }));
  },
}));
